import type {
  MarkExpansion,
  MarkTreeItem,
} from '../../interaction/markNavigation';
import type { NetworkFolded } from '../render/foldedMarks';
import type { NetworkFocus } from '../render/packNetwork';
import type {
  NetworkGraph,
  NetworkNode,
  NetworkNodeId,
  NetworkVolume,
} from '../types';

export type NetworkTreeItem = MarkTreeItem & {
  /** An open region or a closed group: what a double-click folds or unfolds. */
  isGroup: boolean;
};

export type NetworkTreeArgs = {
  graph: NetworkGraph;
  foldedBehind: ((nodeId: NetworkNodeId) => NetworkFolded | null) | undefined;
  members: (count: number) => string;
  /** Rows folded in the tree alone, without changing the graph. */
  collapsedIds: ReadonlySet<NetworkNodeId>;
  /** Whether the host folds groups, which is what Left and Right on a group then drive. */
  canActivate: boolean;
};

type TreeContext = Omit<NetworkTreeArgs, 'graph'> & {
  childrenOf: ReadonlyMap<NetworkNodeId | null, readonly NetworkNode[]>;
};

const toChildrenByParent = (
  nodes: readonly NetworkNode[]
): Map<NetworkNodeId | null, NetworkNode[]> => {
  const ids = new Set(nodes.map(({ id }) => id));
  return nodes.reduce((byParent, node) => {
    const parentId =
      node.parentId !== null && ids.has(node.parentId) ? node.parentId : null;
    const siblings = byParent.get(parentId);
    if (siblings) siblings.push(node);
    else byParent.set(parentId, [node]);
    return byParent;
  }, new Map<NetworkNodeId | null, NetworkNode[]>());
};

const toExpansion = (
  folded: NetworkFolded | null,
  hasChildren: boolean,
  isCollapsedInTree: boolean,
  canActivate: boolean
): MarkExpansion => {
  if (folded !== null) return canActivate ? 'collapsed' : 'leaf';
  if (!hasChildren) return 'leaf';
  return isCollapsedInTree ? 'collapsed' : 'expanded';
};

const toDetails = (
  node: NetworkNode,
  folded: NetworkFolded | null,
  members: (count: number) => string
): string[] => [
  ...(node.typeName === '' ? [] : [node.typeName]),
  ...(folded === null ? [] : [members(folded.foldedCount)]),
];

const toSubtree = (
  siblings: readonly NetworkNode[],
  parentId: NetworkNodeId | null,
  level: number,
  visited: ReadonlySet<NetworkNodeId>,
  context: TreeContext
): NetworkTreeItem[] =>
  siblings
    .filter(({ id }) => !visited.has(id))
    .flatMap((node, index, listed) => {
      const children = context.childrenOf.get(node.id) ?? [];
      const folded = context.foldedBehind?.(node.id) ?? null;
      const hasChildren = children.length > 0;
      const expansion = toExpansion(
        folded,
        hasChildren,
        context.collapsedIds.has(node.id),
        context.canActivate
      );
      const item: NetworkTreeItem = {
        id: node.id,
        label: node.label,
        details: toDetails(node, folded, context.members),
        parentId,
        level,
        setSize: listed.length,
        posInSet: index + 1,
        expansion,
        isGroup: folded !== null || (node.weight === 0 && hasChildren),
      };
      if (expansion !== 'expanded') return [item];

      const descendantsVisited = new Set([...visited, node.id]);
      return [
        item,
        ...toSubtree(children, node.id, level + 1, descendantsVisited, context),
      ];
    });

/** The visible rows of the groups-and-components tree, in the graph's own order. */
export const toNetworkTreeItems = ({
  graph,
  ...rest
}: NetworkTreeArgs): NetworkTreeItem[] => {
  const childrenOf = toChildrenByParent(graph.nodes);
  return toSubtree(childrenOf.get(null) ?? [], null, 1, new Set(), {
    ...rest,
    childrenOf,
  });
};

export type NetworkTreeFocusArgs = {
  nodes: readonly NetworkNode[];
  volumes: readonly NetworkVolume[];
  nodeId: NetworkNodeId | null;
};

/** What the canvas lights for a tree row: the mark, or the region an open group occupies. */
export const toNetworkTreeFocus = ({
  nodes,
  volumes,
  nodeId,
}: NetworkTreeFocusArgs): NetworkFocus | null => {
  if (nodeId === null) return null;
  const index = nodes.findIndex(({ id }) => id === nodeId);
  if (index === -1) return null;
  if (nodes[index].weight !== 0) return { kind: 'node', index };

  const volumeIndex = volumes.findIndex(volume => volume.nodeIndex === index);
  if (volumeIndex === -1) return null;
  return { kind: 'region', index: volumeIndex };
};

export type TreeToggleArgs = {
  item: NetworkTreeItem | undefined;
  canActivate: boolean;
};

/** Whether folding a row is the host's double-click action or only the tree's own. */
export const toTreeToggle = ({
  item,
  canActivate,
}: TreeToggleArgs): 'activate' | 'tree' | null => {
  if (!item) return null;
  return item.isGroup && canActivate ? 'activate' : 'tree';
};

export const toggleId = (
  ids: ReadonlySet<NetworkNodeId>,
  id: NetworkNodeId
): ReadonlySet<NetworkNodeId> => {
  const next = new Set(ids);
  if (next.has(id)) {
    next.delete(id);
    return next;
  }
  next.add(id);
  return next;
};

export type NetworkMarkIndicesArgs = {
  nodes: readonly NetworkNode[];
  volumes: readonly NetworkVolume[];
  /** Node ids, as the tree names its rows. */
  ids: readonly NetworkNodeId[];
};

/**
 * The marks a set of tree rows stands for, as indices into `positions`.
 *
 * A component is its own mark. A group is not drawn as one, so it stands for everything inside
 * it - which is what makes searching a group's name useful: "finance" lights the finance estate
 * rather than lighting nothing because no mark is called that.
 */
export const toNetworkMarkIndices = ({
  nodes,
  volumes,
  ids,
}: NetworkMarkIndicesArgs): number[] => {
  const indexById = new Map(nodes.map(({ id }, index) => [id, index]));
  const marks = new Set<number>();

  for (const id of ids) {
    const index = indexById.get(id);
    if (index === undefined) continue;
    if (nodes[index]?.weight !== 0) {
      marks.add(index);
      continue;
    }
    const volume = volumes.find(({ nodeIndex }) => nodeIndex === index);
    for (const markIndex of volume?.markIndices ?? []) marks.add(markIndex);
  }

  return [...marks];
};
